import { addDays, addMonths, endOfDay, format, isAfter, lastDayOfMonth, setHours, setMinutes, startOfDay } from 'date-fns';

export const WEEKDAY_OPTIONS = [
  { code: 'SU', label: 'Sunday' },
  { code: 'MO', label: 'Monday' },
  { code: 'TU', label: 'Tuesday' },
  { code: 'WE', label: 'Wednesday' },
  { code: 'TH', label: 'Thursday' },
  { code: 'FR', label: 'Friday' },
  { code: 'SA', label: 'Saturday' },
] as const;

export type WeekdayCode = typeof WEEKDAY_OPTIONS[number]['code'];

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

export type RecurrenceConfig = {
  frequency: RecurrenceFrequency;
  weekdays?: WeekdayCode[];
  endDate?: string | null;
};

export type RecurrencePlan = {
  weekdays: WeekdayCode[];
  recurrenceRule: string;
  recurrenceEndsAt?: string;
  occurrences: RecurrenceOccurrence[];
};

export type RecurrenceOccurrence = {
  startTime: string;
  endTime: string;
};

export const toDateTimeInput = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

export const parseDateTimeInput = (value: string) => new Date(value);

export const normalizeIsoDateTime = (value?: string) => {
  if (!value) return undefined;
  return value.length === 16 ? `${value}:00` : value;
};

export const parseDateTimeValue = (value?: string) => {
  if (!value) return undefined;
  const normalized = normalizeIsoDateTime(value);
  if (!normalized) return undefined;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const extractTimeFromIso = (value?: string) => {
  const date = parseDateTimeValue(value);
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const extractDateFromIso = (value?: string) => parseDateTimeValue(value);

export const isCompleteTime = (time: string) => /^\d{2}:\d{2}$/.test(time);

export const combineDateAndTime = (date: Date | undefined, time: string | undefined) => {
  if (!date || !time) return undefined;
  const [hours, minutes] = time.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return undefined;
  const combined = new Date(date);
  combined.setHours(hours!, minutes!, 0, 0);
  return combined;
};

export const combineDefaultTimes = (anchor?: Date) => {
  const start = anchor ? new Date(anchor) : new Date();
  if (!anchor) {
    start.setHours(10, 0, 0, 0);
  }
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { start, end } as const;
};

export const ensureValidDuration = (start: Date, end: Date) => {
  if (end <= start) {
    throw new Error('End time must be after start time');
  }
};

export const weekdayFromDate = (date: Date): WeekdayCode => WEEKDAY_OPTIONS[date.getDay()].code;

/**
 * Gets the weekday code based on UTC time.
 * Use this for server-side recurrence calculations to avoid timezone issues.
 */
export const weekdayFromDateUTC = (date: Date): WeekdayCode => WEEKDAY_OPTIONS[date.getUTCDay()].code;

/**
 * Calculates the day offset between local time and UTC for a given date.
 * Returns:
 *  - 0 if the local day is the same as UTC day
 *  - 1 if the local day is ahead of UTC (e.g., UTC+8 after 4PM UTC)
 *  - -1 if the local day is behind UTC (e.g., UTC-3 after 9PM local = next day UTC)
 */
export const getLocalToUtcDayOffset = (date: Date): number => {
  const localDay = date.getDay();
  const utcDay = date.getUTCDay();
  
  if (localDay === utcDay) return 0;
  
  // Handle wrap-around (e.g., local Saturday, UTC Sunday)
  const diff = utcDay - localDay;
  if (diff > 3) return -1; // e.g., localDay=6 (Sat), utcDay=0 (Sun) -> diff=-6, really means UTC is 1 day ahead
  if (diff < -3) return 1; // e.g., localDay=0 (Sun), utcDay=6 (Sat) -> diff=6, really means UTC is 1 day behind
  
  return diff > 0 ? diff : diff;
};

const WEEKDAY_CODE_TO_INDEX: Record<WeekdayCode, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
};

/**
 * Translates local weekday codes to UTC weekday codes based on the time of day.
 * 
 * When an event starts late in the evening in a timezone behind UTC (like Brasilia UTC-3),
 * the UTC day may be the next day. This function adjusts the weekday codes accordingly.
 * 
 * Example: Event at 9PM Tuesday in Brasilia (UTC-3) = midnight Wednesday UTC.
 * If user selects [TU, TH], this returns [WE, FR] because those are the UTC days.
 * 
 * @param weekdays - The weekday codes as selected by the user (in local time)
 * @param referenceDate - The event start date/time (used to calculate day offset)
 * @returns The weekday codes adjusted to UTC
 */
export const translateWeekdaysToUTC = (
  weekdays: WeekdayCode[],
  referenceDate: Date,
): WeekdayCode[] => {
  const offset = getLocalToUtcDayOffset(referenceDate);
  
  if (offset === 0) return weekdays;
  
  return weekdays.map((code) => {
    const localIndex = WEEKDAY_CODE_TO_INDEX[code];
    // Add offset and wrap around (0-6)
    const utcIndex = ((localIndex + offset) % 7 + 7) % 7;
    return WEEKDAY_OPTIONS[utcIndex].code;
  });
};

/**
 * Translates UTC weekday codes back to local weekday codes based on the time of day.
 * This is the inverse of translateWeekdaysToUTC.
 * 
 * @param weekdays - The weekday codes as stored in the database (in UTC)
 * @param referenceDate - The event start date/time (used to calculate day offset)
 * @returns The weekday codes adjusted to local time
 */
export const translateWeekdaysFromUTC = (
  weekdays: WeekdayCode[],
  referenceDate: Date,
): WeekdayCode[] => {
  const offset = getLocalToUtcDayOffset(referenceDate);
  
  if (offset === 0) return weekdays;
  
  // Invert the offset to go from UTC to local
  return weekdays.map((code) => {
    const utcIndex = WEEKDAY_CODE_TO_INDEX[code];
    // Subtract offset (reverse of translateWeekdaysToUTC)
    const localIndex = ((utcIndex - offset) % 7 + 7) % 7;
    return WEEKDAY_OPTIONS[localIndex].code;
  });
};

export const parseDateOnly = (value?: string | Date | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const [yearStr, monthStr, dayStr] = value.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;
  const day = Number(dayStr);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return undefined;
  }
  const result = new Date(year, month, day, 0, 0, 0, 0);
  return Number.isNaN(result.getTime()) ? undefined : result;
};

export const toDateOnlyString = (date: Date) => format(date, 'yyyy-MM-dd');

/**
 * Resolves the weekdays for a recurring event.
 * Uses UTC-based weekday calculation when deriving from reference date to ensure
 * consistency with occurrence generation.
 */
export const resolveRecurringWeekdays = (
  frequency: RecurrenceFrequency,
  weekdays: WeekdayCode[] | undefined,
  referenceDate: Date,
): WeekdayCode[] => {
  if (frequency === 'weekly') {
    // Use UTC weekday to match occurrence generation logic
    const unique = Array.from(new Set(weekdays && weekdays.length ? weekdays : [weekdayFromDateUTC(referenceDate)]));
    return unique as WeekdayCode[];
  }
  // For 'daily' and 'monthly', weekdays are not applicable
  return weekdays ? Array.from(new Set(weekdays)) as WeekdayCode[] : [];
};

export const buildRecurrenceRule = (frequency: RecurrenceFrequency, weekdays: WeekdayCode[], referenceDate?: Date) => {
  if (frequency === 'daily') {
    return 'FREQ=DAILY;INTERVAL=1';
  }
  if (frequency === 'monthly') {
    const dayOfMonth = referenceDate ? referenceDate.getUTCDate() : 1;
    return `FREQ=MONTHLY;BYMONTHDAY=${dayOfMonth};INTERVAL=1`;
  }
  if (!weekdays.length) {
    return 'FREQ=WEEKLY;INTERVAL=1';
  }
  return `FREQ=WEEKLY;BYDAY=${weekdays.join(',')};INTERVAL=1`;
};

export const resolveRecurrenceEndIso = (endDate?: string | null, fallback?: Date) => {
  const parsed = parseDateOnly(endDate);
  if (parsed) {
    return endOfDay(parsed).toISOString();
  }
  return fallback ? endOfDay(fallback).toISOString() : undefined;
};

/**
 * Generates occurrence dates for recurring events.
 * 
 * Uses UTC-based day calculation to ensure consistent behavior regardless
 * of server timezone. The weekdays are matched against the UTC day of each
 * potential occurrence based on the start time's UTC hour/minute.
 */
const generateOccurrences = (
  startInstance: Date,
  endInstance: Date,
  frequency: RecurrenceFrequency,
  weekdays: WeekdayCode[],
  rawEndDate?: string | null,
) => {
  const recurrenceEndDate = parseDateOnly(rawEndDate);
  if (!recurrenceEndDate) {
    return [{ startTime: startInstance.toISOString(), endTime: endInstance.toISOString() }];
  }

  const boundary = endOfDay(recurrenceEndDate);
  const durationMs = endInstance.getTime() - startInstance.getTime();
  if (durationMs <= 0) throw new Error('End time must be after start time');

  // Monthly: iterate month-by-month using addMonths
  if (frequency === 'monthly') {
    const targetDayOfMonth = startInstance.getUTCDate();
    const occurrences: RecurrenceOccurrence[] = [];
    let monthOffset = 0;

    while (true) {
      // Start from the 1st of the start month, then advance by monthOffset
      const baseMonth = addMonths(
        new Date(Date.UTC(startInstance.getUTCFullYear(), startInstance.getUTCMonth(), 1, 0, 0, 0, 0)),
        monthOffset,
      );
      const lastDay = lastDayOfMonth(baseMonth).getUTCDate();
      const day = Math.min(targetDayOfMonth, lastDay);
      const cursor = new Date(Date.UTC(
        baseMonth.getUTCFullYear(),
        baseMonth.getUTCMonth(),
        day,
        startInstance.getUTCHours(),
        startInstance.getUTCMinutes(),
        0, 0,
      ));

      if (isAfter(cursor, boundary)) break;

      if (cursor >= startInstance) {
        occurrences.push({
          startTime: cursor.toISOString(),
          endTime: new Date(cursor.getTime() + durationMs).toISOString(),
        });
      }

      monthOffset++;
    }

    return occurrences.length
      ? occurrences
      : [{ startTime: startInstance.toISOString(), endTime: endInstance.toISOString() }];
  }

  const allowedWeekdays = frequency === 'daily' ? undefined : new Set(weekdays);
  
  // Use UTC-based iteration to avoid timezone inconsistencies
  // Start from midnight UTC on the start date
  const startUTCDate = new Date(Date.UTC(
    startInstance.getUTCFullYear(),
    startInstance.getUTCMonth(),
    startInstance.getUTCDate(),
    0, 0, 0, 0
  ));
  
  let cursor = startUTCDate;
  const occurrences: RecurrenceOccurrence[] = [];
  
  while (!isAfter(cursor, boundary)) {
    // Check weekday in UTC
    if (!allowedWeekdays || allowedWeekdays.has(weekdayFromDateUTC(cursor))) {
      // Create occurrence at the same UTC hour/minute as the original start
      const start = new Date(Date.UTC(
        cursor.getUTCFullYear(),
        cursor.getUTCMonth(),
        cursor.getUTCDate(),
        startInstance.getUTCHours(),
        startInstance.getUTCMinutes(),
        0, 0
      ));
      
      if (start >= startInstance || occurrences.length) {
        if (start > boundary && occurrences.length) break;
        occurrences.push({
          startTime: start.toISOString(),
          endTime: new Date(start.getTime() + durationMs).toISOString(),
        });
      }
    }
    cursor = addDays(cursor, 1);
  }

  return occurrences.length ? occurrences : [{ startTime: startInstance.toISOString(), endTime: endInstance.toISOString() }];
};

export const prepareRecurrencePlan = (
  startInstance: Date,
  endInstance: Date,
  config: RecurrenceConfig,
): RecurrencePlan => {
  ensureValidDuration(startInstance, endInstance);

  const weekdays = resolveRecurringWeekdays(config.frequency, config.weekdays, startInstance);
  const occurrences = generateOccurrences(startInstance, endInstance, config.frequency, weekdays, config.endDate);
  const recurrenceRule = buildRecurrenceRule(config.frequency, weekdays, startInstance);
  const recurrenceEndsAt = resolveRecurrenceEndIso(config.endDate, endInstance);

  return {
    weekdays,
    recurrenceRule,
    recurrenceEndsAt,
    occurrences,
  };
};


